import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

import { IndicatorNumber, TextInfoGroupAlt } from "@/components/shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { convertBpToDecimal, enumToClientText, formatNumberLocale, sortByShiftType } from "@/utils/helpers";

import { CarPurchasePriceDetails, CarDetails, ShiftType } from "@/graphql/__generated__/graphql";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface CarsSectionProps {
  /** List of car details assigned to the service */
  cars: CarDetails[];
  /** Service-level additional price details used for car related breakdowns */
  carPurchasePriceDetails: CarPurchasePriceDetails[];
  /** Currently selected service id (used to pick the correct ratio link for each car) */
  serviceId: number;
}

/**
 * CarsSection - Renders an accordion section containing a list of collapsible car breakdown rows.
 * Each car row expands to show the car details and a type-grouped car-only additional pricing breakdown.
 *
 * @component
 * @example
 * <CarsSection cars={service.cars} carPurchasePriceDetails={service.pricing.carPurchasePriceDetails} serviceId={service.id} />
 */
function CarsSection({ cars, carPurchasePriceDetails, serviceId }: CarsSectionProps) {
  const intl = useIntl();
  const { t } = useTranslation(["translation", "common"]);
  const [openByCarId, setOpenByCarId] = useState<Record<number, boolean>>({});

  const sortedCarPurchasePriceDetails = useMemo(() => {
    return sortByShiftType(carPurchasePriceDetails ?? []);
  }, [carPurchasePriceDetails]);

  const hasCars = (cars?.length ?? 0) > 0;

  return (
    <Card className="shadow-none">
      <Accordion type="single" collapsible>
        <AccordionItem value="cars">
          <AccordionTrigger className="hover:no-underline hover:bg-slate-50 px-4 py-4">
            <div className="flex items-center gap-2 w-full">
              <div className="text-sm font-bold grow">{t("edittender.calculationoverview.components.cars.title")}</div>
              <IndicatorNumber number={cars.length} />
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pt-2 pb-4">
            {!hasCars ? (
              <div className="text-sm text-slate-500 text-center pt-4">
                {t("edittender.calculationoverview.components.cars.empty")}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {cars.map((carDetails) => {
                  const car = carDetails.car;
                  if (!car) return null;

                  const ratioBp = carDetails.links?.find((link) => link.serviceId === serviceId)?.ratio;
                  const ratioPercent = ratioBp === null || ratioBp === undefined ? undefined : convertBpToDecimal(ratioBp);

                  const isOpen = openByCarId[car.id] ?? false;

                  const carDetailsMetrics = [
                    {
                      key: "quantity",
                      label: t("edittender.calculationoverview.common.metrics.quantity"),
                      value: formatNumberLocale(intl, car.quantity ?? 0, "decimal", false, true),
                    },
                    {
                      key: "runtime",
                      label: t("edittender.calculationoverview.components.cars.labels.runtime"),
                      value: formatNumberLocale(intl, car.runtime ?? 0, "decimal", false, true),
                    },
                    {
                      key: "yearlykilometers",
                      label: t("edittender.calculationoverview.components.cars.labels.yearlykilometers"),
                      value: formatNumberLocale(intl, car.yearlyKilometers ?? 0, "decimal"),
                    },
                    {
                      key: "yearlyinsurance",
                      label: t("edittender.calculationoverview.components.cars.labels.yearlyinsurance"),
                      value: formatNumberLocale(intl, car.yearlyInsurance ?? 0, "currency"),
                    },
                    {
                      key: "yearlytax",
                      label: t("edittender.calculationoverview.components.cars.labels.yearlytax"),
                      value: formatNumberLocale(intl, car.yearlyTax ?? 0, "currency"),
                    },
                    {
                      key: "fuelprice",
                      label: t("edittender.calculationoverview.components.cars.labels.fuelprice"),
                      value: formatNumberLocale(intl, car.fuelPrice ?? 0, "currency"),
                    },
                    {
                      key: "consumption",
                      label: t("edittender.calculationoverview.components.cars.labels.consumption"),
                      value: formatNumberLocale(intl, car.consumption ?? 0, "decimal"),
                    },
                    {
                      key: "repairperkilometer",
                      label: t("edittender.calculationoverview.components.cars.labels.repairperkilometer"),
                      value: formatNumberLocale(intl, car.repairPerKilometer ?? 0, "decimal"),
                    },
                    {
                      key: "netvalue",
                      label: t("edittender.calculationoverview.components.cars.labels.netvalue"),
                      value: formatNumberLocale(intl, car.netValue ?? 0, "currency"),
                    },
                    {
                      key: "revenue",
                      label: t("common:pricing.labels.revenue"),
                      value: formatNumberLocale(intl, car.revenue ?? 0, "currency"),
                    },
                    {
                      key: "totalcost",
                      label: t("edittender.calculationoverview.common.metrics.totalcost"),
                      value: formatNumberLocale(intl, car.totalCost ?? 0, "currency"),
                    },
                    {
                      key: "margin",
                      label: t("common:pricing.labels.margin"),
                      value: formatNumberLocale(intl, convertBpToDecimal(car.margin ?? 0), "decimal", false, false, 0, 2),
                    },
                  ];

                  return (
                    <Card key={`car-collapsible-${car.id}`} className="shadow-none">
                      <Collapsible
                        open={isOpen}
                        onOpenChange={(open) => setOpenByCarId((prev) => ({ ...prev, [car.id]: open }))}
                      >
                        <CollapsibleTrigger asChild>
                          <CardHeader className="px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-2 grow justify-between">
                                <span className="text-sm font-semibold break-all">{car.name}</span>
                                <Badge variant="secondary">
                                  {t("edittender.calculationoverview.common.metrics.ratio")}: &nbsp;
                                  {ratioPercent === undefined ? "—" : `${formatNumberLocale(intl, ratioPercent, "decimal", false, false, 0, 2)}%`}
                                </Badge>
                              </div>
                              {isOpen ? (
                                <ChevronUpIcon className="size-4 shrink-0 text-slate-500" />
                              ) : (
                                <ChevronDownIcon className="size-4 shrink-0 text-slate-500" />
                              )}
                            </div>
                          </CardHeader>
                        </CollapsibleTrigger>

                        <CollapsibleContent>
                          <CardContent className="px-4 pb-4 pt-0">
                            <Separator className="mb-4" />

                            {/* Car card (based on example data) */}
                            <Card className="shadow-none p-2">
                              <CardHeader className="p-0 pb-3">
                                <CardTitle className="text-sm font-bold">
                                  {t("edittender.calculationoverview.components.cars.cardtitle")}
                                </CardTitle>
                              </CardHeader>
                              <CardContent className="p-0">
                                <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))" }}>
                                  {carDetailsMetrics.map((metric) => (
                                    <TextInfoGroupAlt
                                      key={metric.key}
                                      label={metric.label}
                                      content={metric.value}
                                      hasBackground={true}
                                      backgroundColor="slate"
                                    />
                                  ))}
                                </div>
                              </CardContent>
                            </Card>

                            <Separator className="my-4" />

                            {/* Additional price details (car-only fields grouped by type) */}
                            <div className="flex flex-col gap-2">
                              <div className="text-sm font-bold">
                                {t("edittender.calculationoverview.components.cars.additionalbreakdown")}
                              </div>

                              {sortedCarPurchasePriceDetails.map((detail) => {
                                const typeLabel = enumToClientText(detail.type);

                                return (
                                  <div key={`car-additional-${detail.type}`}>
                                    <div
                                      className={cn(
                                        "flex flex-wrap items-center rounded-md px-2 py-1",
                                        detail.type === ShiftType.Cumulated ? "bg-slate-50 hover:bg-slate-100" : "hover:bg-slate-50"
                                      )}
                                    >
                                      <div className="mb-1 w-1/4 font-semibold">
                                        {t(`common:costdetailstype.${detail.type.toLowerCase()}`) || typeLabel}
                                      </div>
                                      <div className="flex w-3/4 flex-wrap gap-x-4 gap-y-2">
                                        <TextInfoGroupAlt
                                          className="w-[150px]"
                                          label={t("common:pricing.labels.costs")}
                                          content={formatNumberLocale(intl, detail.carCosts ?? 0, "currency")}
                                        />
                                        <TextInfoGroupAlt
                                          className="w-[150px]"
                                          label={t("common:pricing.labels.revenue")}
                                          content={formatNumberLocale(intl, detail.carRevenue ?? 0, "currency")}
                                        />
                                      </div>
                                    </div>
                                    <Separator className="my-2" />
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        </CollapsibleContent>
                      </Collapsible>
                    </Card>
                  );
                })}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

export default CarsSection;
